import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MagnifyingGlass, ArrowClockwise, ChartBar, Brain, ShareNetwork, WaveSine } from "@phosphor-icons/react";
import DateRangePicker, { todayRange, rangeToParams } from "@/components/DateRangePicker";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";

const RATIO_KEYS = [
  { key: "transfer_success_ratio", label: "Transfer ✓ %", color: "#10b981" },
  { key: "transfer_failed_ratio", label: "Transfer ✗ %", color: "#f43f5e" },
  { key: "vm_ratio", label: "Voicemail %", color: "#f59e0b" },
  { key: "hangup_ratio", label: "Hangup %", color: "#0ea5e9" },
];
const tooltipStyle = {
  background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))",
  borderRadius: 10, fontSize: 12, color: "hsl(var(--popover-foreground))",
};
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 11 };
const secs = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

function QualitySection({ rows, icon: Icon, nameLabel, testid }) {
  if (!rows || rows.length === 0)
    return <EmptyState testid={`${testid}-empty`} icon={Icon} title="No data"
      description="No calls matched these filters for this resource type." />;
  return (
    <div className="space-y-5" data-testid={testid}>
      <div className="rounded-xl border border-border bg-card card-shadow p-5">
        <h3 className="text-sm font-semibold mb-4">Quality ratios by {nameLabel.toLowerCase()}</h3>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={axis} axisLine={false} tickLine={false} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={axis} axisLine={false} tickLine={false} width={34} unit="%" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {RATIO_KEYS.map((r) => (
                <Bar key={r.key} dataKey={r.key} name={r.label} fill={r.color} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid={`${testid}-table`}>
        <Table>
          <TableHeader><TableRow>
            <TableHead>{nameLabel}</TableHead>
            <TableHead className="text-right">Total calls</TableHead>
            <TableHead className="text-right">Min dur</TableHead>
            <TableHead className="text-right">Avg dur</TableHead>
            <TableHead className="text-right">Transfer ✓</TableHead>
            <TableHead className="text-right">Transfer ✗</TableHead>
            <TableHead className="text-right">Voicemail</TableHead>
            <TableHead className="text-right">Hangup</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`${testid}-row-${r.id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-right tabular-nums">{r.total}</TableCell>
                <TableCell className="text-right tabular-nums">{secs(r.min_duration)}</TableCell>
                <TableCell className="text-right tabular-nums">{secs(r.avg_duration)}</TableCell>
                <TableCell className="text-right tabular-nums text-emerald-600">{r.transfer_succeeded} <span className="text-muted-foreground">({r.transfer_success_ratio}%)</span></TableCell>
                <TableCell className="text-right tabular-nums text-rose-600">{r.transfer_failed} <span className="text-muted-foreground">({r.transfer_failed_ratio}%)</span></TableCell>
                <TableCell className="text-right tabular-nums text-amber-600">{r.voicemail} <span className="text-muted-foreground">({r.vm_ratio}%)</span></TableCell>
                <TableCell className="text-right tabular-nums text-sky-600">{r.hangup} <span className="text-muted-foreground">({r.hangup_ratio}%)</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function StatsReport() {
  const [range, setRange] = useState(todayRange());
  const [appliedRange, setAppliedRange] = useState(todayRange());
  const [filters, setFilters] = useState({ customer: "", campaign: "", ai_engine: "" });
  const [applied, setApplied] = useState({ customer: "", campaign: "", ai_engine: "" });
  const [engines, setEngines] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ai-engines/overview").then(({ data }) => setEngines(data.engines || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...rangeToParams(appliedRange) };
      Object.entries(applied).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/cdr/quality-stats", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail)); setData(null);
    } finally { setLoading(false); }
  }, [applied, appliedRange]);
  useEffect(() => { load(); }, [load]);

  const apply = () => { setApplied(filters); setAppliedRange(range); };
  const reset = () => {
    const f = { customer: "", campaign: "", ai_engine: "" };
    setFilters(f); setApplied(f); setRange(todayRange()); setAppliedRange(todayRange());
  };

  const o = data?.overall;
  const overallCards = o ? [
    ["Total calls", o.total],
    ["Avg duration", secs(o.avg_duration)],
    ["Transfer success", `${o.transfer_success_ratio}%`],
    ["Transfer failed", `${o.transfer_failed_ratio}%`],
    ["Voicemail", `${o.vm_ratio}%`],
    ["Hangup", `${o.hangup_ratio}%`],
  ] : [];

  return (
    <div data-testid="stats-report-page">
      <PageHeader title="Stats Report"
        subtitle="System & call-quality across AI Engines, OpenSIPS and Media servers (UTC). Ratios are of total calls in range." />

      {/* Filters */}
      <div className="rounded-xl border border-border bg-card card-shadow p-4 mb-5 grid gap-3 md:grid-cols-5" data-testid="stats-filters">
        <div className="space-y-1 md:col-span-2">
          <Label className="text-xs">Date range</Label>
          <DateRangePicker value={range} onChange={setRange} testid="stats-daterange" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Customer</Label>
          <Input data-testid="stats-filter-customer" value={filters.customer} placeholder="Customer name"
            onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && apply()} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Campaign</Label>
          <Input data-testid="stats-filter-campaign" value={filters.campaign} placeholder="id / prefix"
            onChange={(e) => setFilters({ ...filters, campaign: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && apply()} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">AI Engine</Label>
          <select data-testid="stats-filter-engine" className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={filters.ai_engine} onChange={(e) => setFilters({ ...filters, ai_engine: e.target.value })}>
            <option value="">All</option>
            {engines.map((e) => <option key={e.engine_id} value={e.engine_id}>{e.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-5 flex gap-2">
          <Button size="sm" className="rounded-lg" onClick={apply} data-testid="stats-apply-btn">
            <MagnifyingGlass className="w-4 h-4 mr-1.5" /> Apply
          </Button>
          <Button size="sm" variant="ghost" className="rounded-lg" onClick={reset} data-testid="stats-reset-btn">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Reset
          </Button>
        </div>
      </div>

      {/* Overall summary */}
      {o && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="stats-overall">
          {overallCards.map(([k, v]) => (
            <div key={k} className="rounded-xl border border-border bg-card card-shadow p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground truncate" title={k}>{k}</p>
              <p className="text-2xl font-semibold tabular-nums mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : !o ? (
          <EmptyState testid="stats-empty" icon={ChartBar} title="No calls in this range"
            description="Adjust the date range or filters to see system quality metrics." />
        ) : (
          <Tabs defaultValue="ai" data-testid="stats-tabs">
            <TabsList>
              <TabsTrigger value="ai" data-testid="stats-tab-ai"><Brain className="w-4 h-4 mr-1.5" /> AI Engines</TabsTrigger>
              <TabsTrigger value="opensips" data-testid="stats-tab-opensips"><ShareNetwork className="w-4 h-4 mr-1.5" /> OpenSIPS</TabsTrigger>
              <TabsTrigger value="media" data-testid="stats-tab-media"><WaveSine className="w-4 h-4 mr-1.5" /> Media</TabsTrigger>
            </TabsList>
            <TabsContent value="ai" className="mt-4">
              <QualitySection rows={data?.ai_engines} icon={Brain} nameLabel="AI Engine" testid="stats-ai" />
            </TabsContent>
            <TabsContent value="opensips" className="mt-4">
              <QualitySection rows={data?.opensips} icon={ShareNetwork} nameLabel="OpenSIPS IP" testid="stats-opensips" />
            </TabsContent>
            <TabsContent value="media" className="mt-4">
              <QualitySection rows={data?.media} icon={WaveSine} nameLabel="Media IP" testid="stats-media" />
            </TabsContent>
          </Tabs>
        )}
    </div>
  );
}
